import React from 'react';

interface CardProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  variant?: 'default' | 'secondary' | 'tertiary';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  shadow?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  border?: boolean;
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  hover?: boolean;
  onClick?: () => void;
}

const Card: React.FC<CardProps> = ({
  children,
  className = '',
  style = {},
  variant = 'default',
  padding = 'md',
  shadow = 'md',
  border = true,
  rounded = 'lg',
  hover = false,
  onClick,
}) => {
  const getVariantStyles = (): React.CSSProperties => {
    switch (variant) {
      case 'secondary':
        return {
          backgroundColor: 'var(--bg-secondary)',
          ...style,
        };
      case 'tertiary':
        return {
          backgroundColor: 'var(--bg-tertiary)',
          ...style,
        };
      default:
        return {
          backgroundColor: 'var(--bg-primary)',
          ...style,
        };
    }
  };

  const getPaddingClass = (): string => {
    switch (padding) {
      case 'none':
        return '';
      case 'sm':
        return 'p-3';
      case 'lg':
        return 'p-8';
      default:
        return 'p-6';
    }
  };

  const getShadowClass = (): string => {
    switch (shadow) {
      case 'none':
        return '';
      case 'sm':
        return 'shadow-sm';
      case 'lg':
        return 'shadow-lg';
      case 'xl':
        return 'shadow-xl';
      default:
        return 'shadow-md';
    }
  };

  const getRoundedClass = (): string => {
    switch (rounded) {
      case 'none':
        return '';
      case 'sm':
        return 'rounded-sm';
      case 'md':
        return 'rounded-md';
      case 'lg':
        return 'rounded-lg';
      case 'xl':
        return 'rounded-xl';
      case '2xl':
        return 'rounded-2xl';
      default:
        return 'rounded-lg';
    }
  };

  const cardClasses = [
    'card',
    getPaddingClass(),
    getShadowClass(),
    getRoundedClass(),
    border ? 'border' : '',
    hover ? 'hover:shadow-xl hover:scale-105 transition-all duration-200' : '',
    onClick ? 'cursor-pointer' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const cardStyle: React.CSSProperties = {
    ...getVariantStyles(),
    ...(border && { borderColor: 'var(--border)' }),
    ...(shadow !== 'none' && { boxShadow: 'var(--shadow)' }),
  };

  return (
    <div className={cardClasses} style={cardStyle} onClick={onClick}>
      {children}
    </div>
  );
};

export default Card;
